import React, { useCallback, useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/utils2";

const CAT_ICONS = {
  ascenseur: "ArrowUpDown", etancheite: "Droplets", extincteurs: "FireExtinguisher",
  plomberie: "Wrench", electricite: "Zap", nettoyage: "Sparkles",
  espaces_verts: "Trees", autre: "Hammer",
};
const CAT_COLORS = {
  ascenseur: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  etancheite: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300",
  extincteurs: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  plomberie: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
  electricite: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  nettoyage: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  espaces_verts: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  autre: "bg-secondary text-secondary-foreground",
};
const CATS = Object.keys(CAT_ICONS);

const dueStatus = (iso) => {
  if (!iso) return null;
  const days = Math.round((new Date(iso) - new Date()) / 86400000);
  if (days < 0) return "overdue";
  if (days <= 45) return "soon";
  return "ok";
};

export default function Maintenance() {
  const { t, lang } = useApp();
  const { isRole } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // objet ou {} (nouveau) ou null (fermé)

  const canManage = isRole("president") || isRole("tresorier") || isRole("conseil");
  const locale = lang === "ar" ? "ar-MA" : lang === "en" ? "en-GB" : "fr-FR";
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : "—");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/maintenance"); setItems(data); }
    catch { toast.error(t("load_failed")); }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm(t("mnt_confirm_delete"))) return;
    try { await api.delete(`/maintenance/${id}`); toast.success(t("mnt_deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const upcoming = items
    .filter((i) => i.next_due && dueStatus(i.next_due) !== "ok")
    .sort((a, b) => new Date(a.next_due) - new Date(b.next_due));

  return (
    <div className="space-y-5" data-testid="maintenance-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Icons.ClipboardList className="h-6 w-6 text-brand dark:text-primary shrink-0" />
          <h1 className="text-2xl font-bold truncate">{t("maintenance")}</h1>
        </div>
        {canManage && (
          <button onClick={() => setEditing({})} data-testid="mnt-new-btn"
            className="h-10 px-4 rounded-xl bg-brand text-white font-bold text-sm flex items-center gap-1.5 active:scale-95 transition-transform shrink-0">
            <Icons.Plus className="h-4 w-4" /> {t("mnt_new")}
          </button>
        )}
      </div>

      {upcoming.length > 0 && (
        <div className="rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 p-4" data-testid="mnt-upcoming">
          <p className="font-bold text-amber-800 dark:text-amber-300 mb-2 flex items-center gap-2">
            <Icons.CalendarClock className="h-5 w-5" /> {t("mnt_upcoming")}
          </p>
          <div className="space-y-1.5">
            {upcoming.map((i) => (
              <div key={i.id} className="flex items-center justify-between text-sm">
                <span className="truncate pr-2">{i.title}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${dueStatus(i.next_due) === "overdue" ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"}`}>
                  {dueStatus(i.next_due) === "overdue" ? t("mnt_overdue") : t("mnt_due_soon")} · {fmt(i.next_due)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-2xl bg-card border border-border p-10 text-center text-muted-foreground" data-testid="mnt-empty">
          <Icons.ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-40" />
          {t("mnt_none")}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((i) => {
            const Ico = Icons[CAT_ICONS[i.category] || "Hammer"] || Icons.Hammer;
            const ds = dueStatus(i.next_due);
            return (
              <div key={i.id} className="rounded-2xl bg-card border border-border p-4" data-testid={`mnt-card-${i.id}`}>
                <div className="flex items-start gap-3">
                  <span className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${CAT_COLORS[i.category] || CAT_COLORS.autre}`}>
                    <Ico className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-muted-foreground">{t("mnt_cat_" + i.category)}</span>
                      {i.status === "planifie" && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-majorelle/15 text-majorelle">{t("mnt_status_planifie")}</span>}
                    </div>
                    <p className="font-bold">{i.title}</p>
                    {i.description && <p className="text-sm text-muted-foreground mt-0.5">{i.description}</p>}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-2">
                      {i.provider && <span className="flex items-center gap-1"><Icons.Building2 className="h-3.5 w-3.5" /> {i.provider}</span>}
                      <span className="flex items-center gap-1"><Icons.Calendar className="h-3.5 w-3.5" /> {fmt(i.date_done)}</span>
                      {i.cost != null && <span className="flex items-center gap-1 font-semibold text-foreground"><Icons.Coins className="h-3.5 w-3.5" /> {money(i.cost)}</span>}
                    </div>
                    {i.next_due && (
                      <div className="mt-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ds === "overdue" ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" : ds === "soon" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" : "bg-secondary text-secondary-foreground"}`}>
                          {t("mnt_next_due")} : {fmt(i.next_due)}
                        </span>
                      </div>
                    )}
                  </div>
                  {canManage && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <button onClick={() => setEditing(i)} data-testid={`mnt-edit-${i.id}`} className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center"><Icons.Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(i.id)} data-testid={`mnt-delete-${i.id}`} className="h-8 w-8 rounded-full bg-destructive/10 text-destructive flex items-center justify-center"><Icons.Trash2 className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing !== null && <MaintModal t={t} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function MaintModal({ t, initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    category: initial.category || "ascenseur",
    title: initial.title || "",
    description: initial.description || "",
    provider: initial.provider || "",
    phone: initial.phone || "",
    date_done: (initial.date_done || "").slice(0, 10),
    cost: initial.cost ?? "",
    next_due: (initial.next_due || "").slice(0, 10),
    status: initial.status || "fait",
  });
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((p) => ({ ...p, ...patch }));
  const submit = async () => {
    if (!form.title.trim()) { toast.error(t("mnt_title_required")); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        cost: form.cost === "" ? null : Number(form.cost),
        date_done: form.date_done || null,
        next_due: form.next_due || null,
      };
      if (isEdit) { await api.put(`/maintenance/${initial.id}`, payload); toast.success(t("mnt_updated")); }
      else { await api.post("/maintenance", payload); toast.success(t("mnt_created")); }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const field = "w-full h-12 rounded-xl border border-input bg-background px-4 outline-none";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="mnt-modal">
      <div className="bg-card text-foreground rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">{isEdit ? t("mnt_edit") : t("mnt_new")}</h2>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center"><Icons.X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <select value={form.category} onChange={(e) => set({ category: e.target.value })} data-testid="mnt-category" className={field}>
            {CATS.map((c) => <option key={c} value={c}>{t("mnt_cat_" + c)}</option>)}
          </select>
          <input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder={t("mnt_title")} data-testid="mnt-title" className={field} />
          <textarea value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder={t("mnt_desc")} rows={2} className="w-full rounded-xl border border-input bg-background px-4 py-3 outline-none" />
          <div className="grid grid-cols-2 gap-2">
            <input value={form.provider} onChange={(e) => set({ provider: e.target.value })} placeholder={t("mnt_provider")} data-testid="mnt-provider" className={field} />
            <input value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder={t("mnt_phone")} data-testid="mnt-phone" className={field} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-muted-foreground">{t("mnt_date")}
              <input type="date" value={form.date_done} onChange={(e) => set({ date_done: e.target.value })} data-testid="mnt-date" className={field} />
            </label>
            <label className="text-xs text-muted-foreground">{t("mnt_next_due")}
              <input type="date" value={form.next_due} onChange={(e) => set({ next_due: e.target.value })} data-testid="mnt-next-due" className={field} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" min="0" value={form.cost} onChange={(e) => set({ cost: e.target.value })} placeholder={`${t("mnt_cost")} (MAD)`} data-testid="mnt-cost" className={field} />
            <select value={form.status} onChange={(e) => set({ status: e.target.value })} data-testid="mnt-status" className={field}>
              <option value="fait">{t("mnt_status_fait")}</option>
              <option value="planifie">{t("mnt_status_planifie")}</option>
            </select>
          </div>
          <button onClick={submit} disabled={busy} data-testid="mnt-submit" className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">{busy ? "…" : t("mnt_save")}</button>
        </div>
      </div>
    </div>
  );
}
